import 'classlist-polyfill';
import 'focus-visible';
import classnames from 'classnames';
import React, { ButtonHTMLAttributes, useCallback, useMemo } from 'react';

import Icon, { ICON_SIZES, IconProps } from '@agilemd/ui-icon';
import { addVariants, useBoolean, useEventCallback, useSpacing } from '@agilemd/ui-utils';

import styles from './styles.module.scss';

export const COLORS = [
  'white',
  'blue',
  'dark-blue',
  'gray',
  'black',
  'light',
  'transparent',
  'red',
  'green',
  'orange',
  'yellow',
  'blue-hover',
  'dark',
  'hover-darken',
  'hover-lighten'
] as const;
export const SIZES = ['x-small', 'small', 'default', 'large'] as const;
export const VARIANTS = [
  'disabled',
  'flat',
  'square',
  'round',
  'outline',
  'no-padding',
  'dense',
  'fill',
  'active',
  'no-border',
  'even-padding',
  ...COLORS,
  ...SIZES
] as const;

export interface ButtonOverrideProps {
  component?: React.ElementType;
  onChange?: (currentValue: boolean) => void;
  onClick?:
    | React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
    | React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>
    | null;
  children?:
    | React.ReactNode
    | ((state: {
        isSelected: boolean;
        toggleSelected: () => void;
        setSelected: (value: boolean) => void;
      }) => React.ReactNode);
}

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOverrideProps> {
  /**
   * Fired every time the button is clicked
   */
  onClick?:
    | React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
    | React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>
    | null;
  /**
   * Fired when `selectable` and the user just clicked the button,
   * passes the new state the button is being changed to
   */
  onChange?: (currentValue: boolean) => void;
  /**
   * Renders the button as a link
   */
  href?: string | null;
  /**
   * Decides whether the button is a selectable one
   * @deprecated Change the color dynamically instead
   */
  isSelectable?: boolean;
  /**
   * Whether the button is selected, state must be handled by caller
   * @deprecated Change the color dynamically instead
   */
  isSelected?: boolean | null;
  /**
   * Whether the button is selected by default
   * @deprecated Change the color dynamically instead
   */
  isSelectedByDefault?: boolean;
  /**
   * Disables all functionality of the button
   */
  isDisabled?: boolean;
  /**
   * Additional classnames to apply
   */
  className?: string;
  /**
   * Allows specifying an icon
   * @deprecated Use `Button.Icon` instead
   */
  icon?: IconProps['icon'] | null;
  /**
   * Allows specifying where the icon would be placed, if `right` icon will be on the right
   * @deprecated Use `Button.Icon` instead
   */
  alignIcon?: 'left' | 'right' | false;
  /**
   * Specifies the size of the icon
   * @deprecated Use `Button.Icon` instead
   */
  iconSize?: (typeof ICON_SIZES)[number];
  /**
   * Decides the variant of the button
   */
  variant?: (typeof VARIANTS)[number] | boolean | Array<(typeof VARIANTS)[number] | boolean>;
  /**
   * The color of the button
   */
  color?: (typeof COLORS)[number];
  /**
   * Allow customization of the margin around the button. See `Spacer` Component
   */
  spacing?: {
    top?: number | boolean;
    bottom?: number | boolean;
    left?: number | boolean;
    right?: number | boolean;
    x?: number | boolean;
    y?: number | boolean;
  };
  /**
   * Custom styles to apply, use sparingly
   */
  style?: React.CSSProperties;
  /**
   * Inner content of the button
   */
  children?:
    | React.ReactNode
    | ((state: {
        isSelected: boolean;
        toggleSelected: () => void;
        setSelected: (value: boolean) => void;
      }) => React.ReactNode)
    | false;
  /**
   * Render onto an element type
   */
  component?: React.ElementType;
  /**
   * Whether onclick to preventDefault on event
   */
  shouldPreventDefault?: boolean;
  /**
   * Whether onclick to stopPropagation on event
   */
  shouldStopPropagation?: boolean;
}

/**
 * Amazing! Isn't the best button you've ever laid your eyes on?
 *
 * It can have 2 purposes:
 *
 * - *button only*: The button will notify the caller of when the
 * button was clicked through the button's `onClick` handler.
 * - *form input*: The button will maintain an active/selected state internal and
 * notify the callee that a change has been made through the button's `onChange` handler.
 */
const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      onClick: onClickFromProps = null,
      onChange = () => null,
      children = false,
      isSelectable = false,
      isSelected: isSelectedFromProps = null,
      icon = null,
      alignIcon: alignIconFromProps = 'left',
      iconSize = ICON_SIZES[0],
      isDisabled = false,
      href = null,
      className = '',
      variant: variants = 'default',
      color = 'white',
      component: Component = 'button',
      shouldPreventDefault = true,
      shouldStopPropagation = true,
      isSelectedByDefault = false,
      style = {},
      spacing = {},
      ...props
    }: ButtonProps,
    ref
  ) => {
    const spacingStyles = useSpacing(spacing, { style });
    const {
      value: isSelected,
      toggle: toggleSelected,
      set: setSelected
    } = useBoolean(isSelectedByDefault!, {
      onChange,
      overrideWith: isSelectedFromProps
    });

    const onClick = useEventCallback(onClickFromProps!);
    const onClickButton = useCallback(
      (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        if (typeof onClick.current === 'function' && shouldPreventDefault) {
          e.preventDefault();
        }

        if (shouldStopPropagation) {
          e.stopPropagation();
        }

        if (isDisabled) {
          return;
        }

        if (isSelectable) {
          toggleSelected();
        }

        if (typeof onClick.current === 'function') {
          onClick(e);
        }
      },
      [
        isSelectable,
        isDisabled,
        shouldPreventDefault,
        shouldStopPropagation,
        onClick,
        toggleSelected
      ]
    );
    const childrenEl = useMemo(
      () =>
        typeof children === 'function'
          ? children({ isSelected, toggleSelected, setSelected })
          : children,
      [children, isSelected, setSelected, toggleSelected]
    );
    const hasIcon = icon !== null;

    const BaseComponent: React.ElementType = href ? 'a' : Component!;
    let iconEl;

    let alignIcon;

    if (children) {
      alignIcon = alignIconFromProps;
    }

    if (hasIcon) {
      iconEl = (
        <span className={addVariants('aglmd-button__icon__container', styles)}>
          <Icon
            icon={icon!}
            className={addVariants('aglmd-button__icon', styles)}
            size={iconSize}
          />
        </span>
      );

      if (!children) {
        alignIcon = false;
      }
    }

    return (
      <BaseComponent
        ref={ref}
        className={classnames(
          addVariants(
            'aglmd-button',
            styles,
            variants,
            color,
            isDisabled && 'disabled',
            isSelectable && isSelected && 'active',
            !children && icon && 'icon-only',
            hasIcon ? 'has-icon' : 'no-icon',
            `icon-${alignIcon}`
          ),
          className
        )}
        onClick={onClickButton}
        style={spacingStyles}
        tabIndex={isDisabled ? -1 : 0}
        {...(href ? {} : { disabled: isDisabled })}
        onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          if (e.key === 'Enter') {
            onClickButton(e as unknown as React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>);
          }
        }}
        href={href || undefined}
        {...props}
      >
        {alignIcon === 'left' ? iconEl : childrenEl}
        {alignIcon === 'left' ? childrenEl : iconEl}
      </BaseComponent>
    );
  }
);

export default Button;
